/**
 * Audit-Log — the chronicle of every conclusion and assessment so far.
 *
 * Read-only by design: the page never mutates anything, so it is guarded by
 * `triage.read` and is the auditor's natural landing point. The server
 * (`GET /api/audit-log`) reconstructs each step from the VulnerabilityEvent
 * card history, the Benachrichtigung cards and the evidence store; this page
 * only decides how it reads.
 *
 * Three layers, from overview to evidence:
 *   1. the Prüfpfad KPI row — how many decisions, how well they are reasoned,
 *      how fast they came and whether customers were informed in time;
 *   2. the stream — a day-grouped timeline ("Chronik") or one file per case
 *      ("Fallakten") with a four-step progress line;
 *   3. the aside — the current verdict mix and the people involved.
 */

import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { useLingui as useLinguiContext } from '@lingui/react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip, { type ChipTone } from '../components/Chip';
import EmptyState from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchAuditLog } from '../lib/api';
import { fmtDate, fmtScore } from '../lib/format';
import type { AuditCase, AuditEntry, AuditLogResponse, AuditVerdict, Bewertung } from '../lib/types';

import {
  AUDIT_GROUPS,
  KIND_LABELS,
  caseSteps,
  conclusionOf,
  fmtDuration,
  groupByDay,
  groupOf,
  initials,
  isReassessment,
  komponenteLabel,
  matchesAuditSearch,
  matchesCaseSearch,
  percent,
  timeLabel,
  titleOf,
  toCsv,
  toneOf,
  verdictLabel,
  type AuditGroup,
} from './auditlog/auditModel';
import { BEWERTUNG_LABELS, parseScore, scoreTone } from './triage/eventHelpers';

import './auditlog/audit-log.css';

type View = 'chronik' | 'faelle';

/** Case-file filter (Fallakten); the KPI tiles set the same filter. */
type CaseFilter = 'alle' | 'offen' | 'neubewertet' | 'ueberfaellig';

/** Labels are translated at render with `i18n._()`. */
const CASE_FILTERS: readonly { key: CaseFilter; label: MessageDescriptor; match: (entry: AuditCase) => boolean }[] = [
  { key: 'alle', label: msg`Alle`, match: () => true },
  { key: 'offen', label: msg`Offen`, match: (entry) => entry.bewertung === 'OFFEN' },
  { key: 'neubewertet', label: msg`Neubewertet`, match: (entry) => entry.neubewertungen > 0 },
  { key: 'ueberfaellig', label: msg`Meldefrist überfällig`, match: (entry) => entry.fristen.ueberfaellig > 0 },
];

/** Good when all of a non-empty whole made it, a warning otherwise. */
function shareTone(part: number, whole: number): KpiItem['tone'] {
  if (whole === 0) return 'neutral';
  return part >= whole ? 'good' : 'warn';
}

/** Board tiles that switch a list filter. */
type BoardTarget = 'entscheidungen' | Exclude<CaseFilter, 'alle'>;

/** How many timeline entries render before "Weitere laden". */
const PAGE_SIZE = 40;

const VERDICT_ORDER: readonly Bewertung[] = ['OFFEN', 'BETROFFEN', 'NICHT_BETROFFEN', 'BEHOBEN'];

/** `?gruppe=` from a deep link (the dashboard's "Im Audit-Log ansehen"). */
function groupParam(value: string | null): AuditGroup {
  return AUDIT_GROUPS.some((entry) => entry.key === value) ? (value as AuditGroup) : 'alle';
}

function triageLink(cve: string | null): string {
  return cve ? `/triage?q=${encodeURIComponent(cve)}` : '/triage';
}

export function AuditLogPage(): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const toast = useToast();
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('chronik');
  const [searchParams] = useSearchParams();
  // Deep links only seed the filters; the page does not write them back.
  const [group, setGroup] = useState<AuditGroup>(() => groupParam(searchParams.get('gruppe')));
  const [actor, setActor] = useState('');
  const [caseFilter, setCaseFilter] = useState<CaseFilter>('alle');
  const [search, setSearch] = useState(() => searchParams.get('q')?.trim() ?? '');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const requestSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await fetchAuditLog();
      if (seq !== requestSeq.current) return;
      setData(response);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(errorMessage(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter changes start the list at the top again.
  useEffect(() => setLimit(PAGE_SIZE), [group, actor, search, view]);

  const entries = useMemo<AuditEntry[]>(() => {
    if (data === null) return [];
    return data.eintraege.filter(
      (entry) =>
        (group === 'alle' || groupOf(entry) === group) &&
        (actor === '' || entry.actor === actor) &&
        matchesAuditSearch(entry, search),
    );
  }, [data, group, actor, search]);

  const cases = useMemo<AuditCase[]>(() => {
    if (data === null) return [];
    return data.faelle.filter(
      (entry) =>
        CASE_FILTERS.find((filter) => filter.key === caseFilter)!.match(entry) &&
        (actor === '' ||
          data.eintraege.some((e) => e.event_id === entry.event_id && e.actor === actor)) &&
        matchesCaseSearch(entry, search),
    );
  }, [data, actor, search, caseFilter]);

  const caseCounts = useMemo<Record<CaseFilter, number>>(() => {
    const all = data?.faelle ?? [];
    return Object.fromEntries(
      CASE_FILTERS.map((filter) => [filter.key, all.filter(filter.match).length]),
    ) as Record<CaseFilter, number>;
  }, [data]);

  // Board tiles: „Dokumentierte Entscheidungen“ opens the „Bewertungen“ pill of
  // the chronicle, the case tiles open the case files with their filter. A
  // tile is marked while exactly its filter is active; a second click clears it.
  const plain = actor === '' && search.trim() === '';
  const boardSelected: BoardTarget | null =
    plain && view === 'chronik' && group === 'bewertungen'
      ? 'entscheidungen'
      : plain && view === 'faelle' && caseFilter !== 'alle'
        ? caseFilter
        : null;
  const onBoardSelect = (target: BoardTarget): void => {
    const active = boardSelected === target;
    setActor('');
    setSearch('');
    if (target === 'entscheidungen') {
      setView('chronik');
      setGroup(active ? 'alle' : 'bewertungen');
    } else {
      setView('faelle');
      setCaseFilter(active ? 'alle' : target);
    }
  };

  const groupCounts = useMemo<Record<AuditGroup, number>>(() => {
    const counts: Record<AuditGroup, number> = {
      alle: 0,
      bewertungen: 0,
      behoerde: 0,
      meldungen: 0,
      nachweise: 0,
      system: 0,
    };
    for (const entry of data?.eintraege ?? []) {
      counts.alle += 1;
      counts[groupOf(entry)] += 1;
    }
    return counts;
  }, [data]);

  // `locale`: the day labels are translated dates.
  const days = useMemo(() => groupByDay(entries.slice(0, limit)), [entries, limit, locale]);

  const onExport = useCallback((): void => {
    if (entries.length === 0) return;
    const blob = new Blob([toCsv(entries)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    const count = entries.length;
    toast.success(t`${plural(count, { one: '# Eintrag als CSV exportiert.', other: '# Einträge als CSV exportiert.' })}`);
  }, [entries, toast, t]);

  const resetFilters = (): void => {
    setGroup('alle');
    setCaseFilter('alle');
    setActor('');
    setSearch('');
  };
  const filtered = group !== 'alle' || actor !== '' || search.trim() !== '';
  const generatedAt = data === null ? null : fmtDate(data.generated_at);
  const moreCount = Math.min(PAGE_SIZE, entries.length - limit);
  const remaining = entries.length - limit;

  return (
    <>
      <PageHeader
        title="Audit-Log"
        sub={generatedAt === null ? undefined : t`Stand ${generatedAt}`}
        actions={
          <button
            type="button"
            className="small"
            onClick={onExport}
            disabled={entries.length === 0}
            title={t`Gefilterte Chronik als CSV (Semikolon, UTF-8) herunterladen`}
          >
            <Icon name="download" size={14} /> <Trans>CSV exportieren</Trans>
          </button>
        }
      />

      {error !== null ? (
        <Banner kind="error" sub={t`Prüfen Sie die Verbindung zu CMDBuild.`}>
          <Trans>Audit-Log konnte nicht geladen werden: {error}</Trans>
        </Banner>
      ) : null}
      {data !== null && !data.dokumente_verfuegbar ? (
        <Banner kind="warn">
          <Trans>
            Der Nachweisspeicher ist nicht lesbar. Advisory- und Upload-Schritte fehlen in dieser
            Ansicht.
          </Trans>
        </Banner>
      ) : null}

      {data === null ? (
        loading ? (
          <PageSpinner text={t`Audit-Log wird zusammengestellt…`} />
        ) : (
          <EmptyState title={t`Keine Daten geladen`} />
        )
      ) : (
        <>
          <AuditBoard data={data} selected={boardSelected} onSelect={onBoardSelect} />

          <div className="audit-layout">
            <section id="audit-stream" className="card audit-stream" aria-labelledby="audit-stream-title">
              <div className="audit-stream-head">
                <h2 id="audit-stream-title">
                  {view === 'chronik' ? <Trans>Chronik</Trans> : <Trans>Fallakten</Trans>}
                </h2>
                <div className="seg audit-view-toggle" role="group" aria-label={t`Ansicht wählen`}>
                  <button
                    type="button"
                    className={view === 'chronik' ? 'on' : undefined}
                    aria-pressed={view === 'chronik'}
                    onClick={() => setView('chronik')}
                  >
                    <Trans>Chronik</Trans>
                  </button>
                  <button
                    type="button"
                    className={view === 'faelle' ? 'on' : undefined}
                    aria-pressed={view === 'faelle'}
                    onClick={() => setView('faelle')}
                  >
                    <Trans>Fallakten</Trans>
                  </button>
                </div>
              </div>

              <div className="audit-filterbar">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  placeholder={t`CVE, Komponente, Person, Kunde oder Begründung…`}
                  aria-label={t`Audit-Log durchsuchen`}
                />
                <select
                  value={actor}
                  onChange={(event) => setActor(event.currentTarget.value)}
                  aria-label={t`Nach Person filtern`}
                >
                  <option value="">{t`Alle Personen`}</option>
                  {data.kennzahlen.bewerter.map((person) => (
                    <option key={person.name} value={person.name}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </div>

              {view === 'chronik' ? (
                <div className="audit-groups" role="group" aria-label={t`Nach Art filtern`}>
                  {AUDIT_GROUPS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={group === item.key ? 'audit-group on' : 'audit-group'}
                      aria-pressed={group === item.key}
                      onClick={() => setGroup(item.key)}
                    >
                      {i18n._(item.label)}
                      <Chip>{groupCounts[item.key]}</Chip>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="audit-groups" role="group" aria-label={t`Fallakten filtern`}>
                  {CASE_FILTERS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={caseFilter === item.key ? 'audit-group on' : 'audit-group'}
                      aria-pressed={caseFilter === item.key}
                      onClick={() => setCaseFilter(item.key)}
                    >
                      {i18n._(item.label)}
                      <Chip>{caseCounts[item.key]}</Chip>
                    </button>
                  ))}
                </div>
              )}

              <div className="audit-stream-body" aria-live="polite" aria-busy={loading}>
                {view === 'chronik' ? (
                  entries.length === 0 ? (
                    <NoEntries filtered={filtered} onReset={resetFilters} />
                  ) : (
                    <>
                      {days.map((day) => (
                        <section className="audit-day" key={day.key} aria-label={day.label}>
                          <h3 className="audit-day-label">
                            <span>{day.label}</span>
                            <span className="audit-day-count">{day.entries.length}</span>
                          </h3>
                          <ol className="audit-timeline">
                            {day.entries.map((entry) => (
                              <TimelineItem key={entry.id} entry={entry} />
                            ))}
                          </ol>
                        </section>
                      ))}
                      {entries.length > limit ? (
                        <div className="audit-more">
                          <button type="button" className="ghost" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                            <Trans>Weitere {moreCount} von {remaining} Einträgen laden</Trans>
                          </button>
                        </div>
                      ) : null}
                    </>
                  )
                ) : cases.length === 0 ? (
                  <NoEntries filtered={caseFilter !== 'alle' || actor !== '' || search.trim() !== ''} onReset={resetFilters} />
                ) : (
                  <div className="audit-cases">
                    {cases.map((entry) => (
                      <CaseCard key={entry.event_id} entry={entry} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            <aside className="audit-aside" aria-label={t`Zusammenfassung`}>
              <VerdictMix data={data} />
              <People data={data} activeActor={actor} onSelect={setActor} />
            </aside>
          </div>
        </>
      )}
    </>
  );
}

/* --- Prüfpfad board --------------------------------------------------------- */

function AuditBoard({ data, selected, onSelect }: {
  data: AuditLogResponse;
  /** Tile whose filter is active. */
  selected: BoardTarget | null;
  onSelect: (target: BoardTarget) => void;
}): ReactNode {
  const { t } = useLingui();
  const k = data.kennzahlen;
  const reasoned = percent(k.begruendet, k.entscheidungen);
  const offen = k.bewertungslage.OFFEN;
  // Case tiles count case files, like the list they open; event totals go in
  // the tooltip.
  const neubewertet = data.faelle.filter((entry) => entry.neubewertungen > 0).length;
  const ueberfaellig = data.faelle.filter((entry) => entry.fristen.ueberfaellig > 0).length;
  const {
    entscheidungen, faelle, automatisch, nachweise, neubewertungen,
  } = k;
  const overdueDeadlines = k.fristen.ueberfaellig;
  const toggle = (target: BoardTarget, ariaLabel: string): Partial<KpiItem> => ({
    ariaLabel,
    selected: selected === target,
    controls: 'audit-stream',
    onClick: () => onSelect(target),
  });

  // Two rows of four: the actionable counts lead each row (open assessments,
  // overdue reports), the rates and totals follow.
  const tiles: KpiItem[] = [
    {
      key: 'offen',
      label: t`Offene Bewertungen`,
      value: String(offen),
      tone: offen > 0 ? 'warn' : 'good',
      title: t`Fälle, deren Anwendbarkeit noch nicht entschieden ist`,
      ...toggle('offen', t`Offene Bewertungen: ${offen} anzeigen`),
    },
    {
      key: 'entscheidungen',
      label: t`Dokumentierte Entscheidungen`,
      value: entscheidungen,
      note: t`über ${plural(faelle, { one: '# Fall', other: '# Fälle' })}`,
      ...toggle('entscheidungen', t`Dokumentierte Entscheidungen: ${entscheidungen} anzeigen`),
      title: t`${automatisch} automatische Statuswechsel · ${plural(nachweise, { one: '# Nachweis', other: '# Nachweise' })}`,
    },
    {
      key: 'begruendung',
      label: t`Begründungsquote`,
      value: reasoned === null ? '–' : `${reasoned} %`,
      note: reasoned === null ? t`nicht anwendbar` : undefined,
      title: t`Anteil der Bewertungen mit schriftlicher Begründung`,
    },
    {
      key: 'reaktionszeit',
      label: t`Reaktionszeit (Median)`,
      value: fmtDuration(k.reaktionszeit_median_h),
      tone: 'neutral',
      title: t`Zeit vom KEV-Treffer bis zur ersten menschlichen Bewertung, Median über alle Fälle`,
    },
    {
      key: 'ueberfaellig',
      label: t`Meldefrist überfällig`,
      value: String(ueberfaellig),
      tone: ueberfaellig > 0 ? 'alert' : 'good',
      title: t`Fälle mit einer Behördenmeldung, deren Frist abgelaufen ist, ohne dass sie als gemeldet erfasst wurde (${plural(overdueDeadlines, { one: '# Frist', other: '# Fristen' })})`,
      ...toggle('ueberfaellig', t`Meldefrist überfällig: ${ueberfaellig} anzeigen`),
    },
    {
      key: 'neubewertet',
      label: t`Neubewertungen`,
      value: String(neubewertet),
      tone: 'neutral',
      title: t`Bereits entschiedene Fälle, deren Bewertung später geändert wurde (${plural(neubewertungen, { one: '# Neubewertung', other: '# Neubewertungen' })})`,
      ...toggle('neubewertet', t`Neubewertungen: ${neubewertet} anzeigen`),
    },
    {
      key: 'kunden',
      label: t`Kunden informiert`,
      value: `${k.meldungen.versendet} / ${k.meldungen.gesamt}`,
      tone:
        k.meldungen.gesamt === 0
          ? 'neutral'
          : k.meldungen.versendet >= k.meldungen.gesamt
            ? 'good'
            : 'warn',
      title: t`Als versendet gestempelte Kundenmeldungen von allen angelegten`,
    },
    {
      key: 'fristgerecht',
      label: t`Fristgerecht gemeldet`,
      value: `${k.fristen.fristgerecht} / ${k.fristen.gemeldet}`,
      tone: shareTone(k.fristen.fristgerecht, k.fristen.gemeldet),
      title: t`Erfasste Behördenmeldungen (Frühwarnung, Meldung, Abschlussbericht), die vor ihrer Frist lagen`,
    },
  ];

  return <KpiRow counters={tiles} label={t`Kennzahlen des Prüfpfads`} />;
}

/* --- Timeline --------------------------------------------------------------- */

function TimelineItem({ entry }: { entry: AuditEntry }): ReactNode {
  const { t } = useLingui();
  const tone = toneOf(entry);
  const conclusion = conclusionOf(entry);
  const score = parseScore(entry.base_score);
  const reassessed = isReassessment(entry);
  const showTransition =
    entry.bewertung !== null && entry.vorher !== null && entry.vorher !== entry.bewertung;
  const before = verdictLabel(entry.vorher);
  const after = verdictLabel(entry.bewertung);
  const deadline = fmtDate(entry.frist);
  const customers = entry.kunden.join(', ');

  return (
    <li className={`audit-item tone-${tone}${reassessed ? ' reassessed' : ''}`}>
      <span className="audit-node" aria-hidden="true">
        <KindIcon kind={entry.kind} />
      </span>
      <article className="audit-entry">
        <header className="audit-entry-head">
          <Chip>{reassessed ? t`Neubewertung` : KIND_LABELS[entry.kind]}</Chip>
          <time dateTime={entry.ts ?? undefined} title={fmtDate(entry.ts)}>
            {timeLabel(entry.ts)}
          </time>
          {/* The send stamp records no user — show no actor rather than a placeholder. */}
          {entry.actor !== null || entry.actor_type === 'system' ? (
            <Actor name={entry.actor} system={entry.actor_type === 'system'} />
          ) : null}
        </header>

        <h4 className="audit-title">{titleOf(entry)}</h4>

        {showTransition ? (
          <div className="audit-transition" aria-label={t`Von ${before} zu ${after}`}>
            <VerdictPill value={entry.vorher} muted />
            <Icon name="arrow-right" />
            <VerdictPill value={entry.bewertung} />
          </div>
        ) : null}

        {conclusion !== null ? (
          <p className="audit-conclusion">
            <span className="audit-conclusion-label"><Trans>Schluss</Trans></span>
            {conclusion}
          </p>
        ) : null}

        {entry.begruendung ? (
          <blockquote className="audit-reason">
            <span className="audit-reason-label"><Trans>Begründung</Trans></span>
            {entry.begruendung}
          </blockquote>
        ) : entry.kind === 'bewertung' ? (
          <p className="audit-reason-missing"><Trans>Keine Begründung hinterlegt.</Trans></p>
        ) : null}

        <footer className="audit-meta">
          {entry.kind !== 'erkannt' ? (
            <span className="audit-meta-item" title={entry.komponente.hersteller ?? undefined}>
              <ComponentIcon />
              {komponenteLabel(entry)}
            </span>
          ) : null}
          {score !== null && (entry.kind === 'erkannt' || entry.kind === 'bewertung') ? (
            <Chip tone={scoreTone(score)} title={t`CVSS-Basiswert`}>
              CVSS {fmtScore(entry.base_score)}
            </Chip>
          ) : null}
          {entry.aktiv_ausgenutzt && entry.kind === 'erkannt' ? <Chip tone="alert">KEV</Chip> : null}
          {entry.kind === 'behoerdenmeldung' && entry.fristgerecht !== null ? (
            <Chip tone={entry.fristgerecht ? 'good' : 'warn'} title={t`Frist: ${deadline}`}>
              {entry.fristgerecht ? <Trans>fristgerecht</Trans> : <Trans>nach Frist</Trans>}
            </Chip>
          ) : null}
          {entry.frist && entry.kind !== 'behoerdenmeldung' ? (
            <span className="audit-meta-item"><Trans>Frist: {deadline}</Trans></span>
          ) : null}
          {entry.kind === 'erkannt' && entry.kunden.length > 0 ? (
            <span className="audit-meta-item" title={customers}>
              <Trans>potenziell betroffen: {customers}</Trans>
            </span>
          ) : null}
          {entry.kind === 'meldung' && entry.kunden.length > 1 ? (
            <span className="audit-meta-item">{entry.kunden.join(', ')}</span>
          ) : null}
          {entry.detail && (entry.kind === 'versand' || entry.kind === 'advisory' || entry.kind === 'nachweis') ? (
            <span className="audit-meta-item mono">{entry.detail}</span>
          ) : null}
          <Link className="audit-open" to={triageLink(entry.cve_id)}>
            <Trans>In Triage öffnen</Trans>
            <Icon name="chevron-right" size={12} />
          </Link>
        </footer>
      </article>
    </li>
  );
}

const VERDICT_TONE: Readonly<Record<AuditVerdict, ChipTone>> = {
  OFFEN: 'warn',
  UNDER_INVESTIGATION: 'info',
  BETROFFEN: 'alert',
  NICHT_BETROFFEN: 'idle',
  BEHOBEN: 'good',
  OPEN: 'warn',
  AFFECTED: 'alert',
  NOT_AFFECTED: 'idle',
  FIXED: 'good',
  SAVED: 'info',
  RELEASED: 'good',
  WITHDRAWN: 'warn',
  STARTED: 'info',
  DUE_SOON: 'warn',
  OVERDUE: 'alert',
};

/** Verdict chip; `muted` is the superseded verdict of a transition. */
function VerdictPill({ value, muted = false }: { value: AuditVerdict | null; muted?: boolean }): ReactNode {
  useLinguiContext();
  return (
    <Chip tone={muted ? 'neutral' : VERDICT_TONE[value ?? 'OFFEN']} className={muted ? 'audit-verdict-prev' : undefined}>
      {verdictLabel(value)}
    </Chip>
  );
}

function Actor({ name, system }: { name: string | null; system: boolean }): ReactNode {
  const { t } = useLingui();
  const label = name ?? (system ? t`System` : t`nicht erfasst`);
  return (
    <span className={system ? 'audit-actor system' : 'audit-actor'} title={system ? t`Automatischer Schritt` : t`Person`}>
      <span className="audit-avatar" aria-hidden="true">
        {system ? <SystemIcon /> : initials(label)}
      </span>
      {label}
    </span>
  );
}

/* --- Case files ------------------------------------------------------------- */

function CaseCard({ entry }: { entry: AuditCase }): ReactNode {
  const { t } = useLingui();
  const steps = caseSteps(entry);
  const { neubewertungen: reassessments, betroffene_kunden: affectedCustomers } = entry;
  const score = parseScore(entry.base_score);
  return (
    <article className="audit-case">
      <header className="audit-case-head">
        <div className="audit-case-id">
          <span className="audit-case-cve mono">{entry.cve_id ?? 'CVE'}</span>
          <span className="audit-case-komp">
            {komponenteLabel(entry)}
            {entry.komponente.hersteller ? ` · ${entry.komponente.hersteller}` : ''}
          </span>
        </div>
        <VerdictPill value={entry.bewertung} />
      </header>

      <ol className="audit-steps" aria-label={t`Fortschritt des Falls`}>
        {steps.map((step) => (
          <li key={step.label} className={`audit-step ${step.state}`}>
            <span className="audit-step-dot" aria-hidden="true">
              {step.state === 'done' ? <Icon name="check" size={12} /> : null}
            </span>
            <span className="audit-step-label">{step.label}</span>
            <span className="audit-step-hint">{step.hint}</span>
          </li>
        ))}
      </ol>

      {entry.begruendung ? (
        <blockquote className="audit-reason compact" title={entry.begruendung}>
          {entry.begruendung}
        </blockquote>
      ) : null}

      <dl className="audit-case-facts">
        <div>
          <dt><Trans>Erkannt</Trans></dt>
          <dd>{fmtDate(entry.erstellt_am)}</dd>
        </div>
        <div>
          <dt><Trans>Reaktionszeit</Trans></dt>
          <dd>{fmtDuration(entry.reaktionszeit_h)}</dd>
        </div>
        <div>
          <dt><Trans>Entscheidungen</Trans></dt>
          <dd>
            {entry.entscheidungen}
            {reassessments > 0 ? (
              <>
                {' '}
                <Chip tone="warn"><Trans>{reassessments} neu bewertet</Trans></Chip>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt><Trans>Nachweise</Trans></dt>
          <dd>
            {entry.dokumente.attachments}
            {entry.dokumente.advisory ? ' · Advisory' : ''}
          </dd>
        </div>
      </dl>

      <footer className="audit-case-foot">
        {score !== null ? (
          <Chip tone={scoreTone(score)}>CVSS {fmtScore(entry.base_score)}</Chip>
        ) : null}
        <span className="audit-meta-item">
          <Plural value={affectedCustomers} one="# betroffener Kunde" other="# betroffene Kunden" />
        </span>
        <Link className="audit-open" to={triageLink(entry.cve_id)}>
          <Trans>In Triage öffnen</Trans>
          <Icon name="chevron-right" size={12} />
        </Link>
      </footer>
    </article>
  );
}

/* --- Aside ------------------------------------------------------------------ */

function VerdictMix({ data }: { data: AuditLogResponse }): ReactNode {
  useLinguiContext();
  const mix = data.kennzahlen.bewertungslage;
  const total = VERDICT_ORDER.reduce((sum, code) => sum + mix[code], 0);
  return (
    <section className="card audit-mix" aria-labelledby="audit-mix-title">
      <h2 id="audit-mix-title"><Trans>Bewertungslage</Trans></h2>
      <p className="audit-aside-sub"><Plural value={total} one="# Fall" other="# Fälle" /></p>
      <div className="audit-mix-bar" role="img" aria-label={VERDICT_ORDER.map((c) => `${BEWERTUNG_LABELS[c]}: ${mix[c]}`).join(', ')}>
        {total === 0 ? (
          <span className="audit-mix-empty" />
        ) : (
          VERDICT_ORDER.filter((code) => mix[code] > 0).map((code) => (
            <span
              key={code}
              className={`audit-mix-seg v-${code.toLowerCase().replace('_', '-')}`}
              style={{ flexGrow: mix[code] }}
              title={`${BEWERTUNG_LABELS[code]}: ${mix[code]} (${percent(mix[code], total) ?? 0} %)`}
            />
          ))
        )}
      </div>
      <ul className="audit-mix-legend">
        {VERDICT_ORDER.map((code) => (
          <li key={code}>
            <span className={`audit-verdict-dot v-${code.toLowerCase().replace('_', '-')}`} aria-hidden="true" />
            <span>{BEWERTUNG_LABELS[code]}</span>
            <strong>{mix[code]}</strong>
            <span className="audit-mix-pct">{total > 0 ? `${percent(mix[code], total)} %` : '—'}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function People({
  data,
  activeActor,
  onSelect,
}: {
  data: AuditLogResponse;
  activeActor: string;
  onSelect: (name: string) => void;
}): ReactNode {
  const { t } = useLingui();
  const people = data.kennzahlen.bewerter;
  return (
    <section className="card audit-people" aria-labelledby="audit-people-title">
      <h2 id="audit-people-title"><Trans>Beteiligte</Trans></h2>
      {people.length === 0 ? (
        <EmptyState compact title={t`Noch keine dokumentierten Schritte`} />
      ) : (
        <ul>
          {people.map((person) => {
            const active = activeActor === person.name;
            const { name, entscheidungen: decisions } = person;
            const lastSeen = fmtDate(person.letzte);
            return (
              <li key={person.name}>
                <button
                  type="button"
                  className={active ? 'audit-person on' : 'audit-person'}
                  aria-pressed={active}
                  onClick={() => onSelect(active ? '' : person.name)}
                  title={active ? t`Filter aufheben` : t`Nur Schritte von ${name} zeigen`}
                >
                  <span className="audit-avatar lg" aria-hidden="true">
                    {initials(person.name)}
                  </span>
                  <span className="audit-person-body">
                    <span className="audit-person-name">{person.name}</span>
                    <span className="audit-person-meta">
                      {t`${plural(decisions, { one: '# Bewertung', other: '# Bewertungen' })} · zuletzt ${lastSeen}`}
                    </span>
                  </span>
                  <strong className="audit-person-count">
                    {person.aktivitaeten}
                  </strong>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function NoEntries({ filtered, onReset }: { filtered: boolean; onReset: () => void }): ReactNode {
  const { t } = useLingui();
  return (
    <EmptyState
      title={filtered ? t`Keine Einträge für diese Filter` : t`Noch keine Einträge`}
      action={
        filtered ? (
          <button type="button" className="ghost small" onClick={onReset}>
            <Trans>Filter zurücksetzen</Trans>
          </button>
        ) : undefined
      }
    >
      {filtered
        ? <Trans>Andere Suchbegriffe probieren oder die Filter zurücksetzen.</Trans>
        : <Trans>Der KEV-Scan hat noch keine Treffer gemeldet.</Trans>}
    </EmptyState>
  );
}

/* --- Icons ------------------------------------------------------------------ */

function Svg({ children, size = 14 }: { children: ReactNode; size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function KindIcon({ kind }: { kind: AuditEntry['kind'] }): ReactNode {
  switch (kind) {
    case 'erkannt':
      return (
        <Svg>
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-4.2-4.2M11 8v3.5M11 14h.01" />
        </Svg>
      );
    case 'bewertung':
      return (
        <Svg>
          <path d="M12 3v18M5 7h14M5 7l-2.5 6a3 3 0 0 0 5 0L5 7ZM19 7l-2.5 6a3 3 0 0 0 5 0L19 7ZM8 21h8" />
        </Svg>
      );
    case 'wiedereroeffnet':
      return (
        <Svg>
          <path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4" />
        </Svg>
      );
    case 'behoben':
      return (
        <Svg>
          <path d="M12 3 4.5 6v5.5c0 4.3 3.1 8 7.5 9.5 4.4-1.5 7.5-5.2 7.5-9.5V6L12 3Z" />
          <path d="m8.8 12 2.2 2.2 4.2-4.4" />
        </Svg>
      );
    case 'meldung':
      return (
        <Svg>
          <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16ZM10 20.5h4" />
        </Svg>
      );
    case 'versand':
      return (
        <Svg>
          <path d="M21 3 10 14M21 3l-6.5 18-4.5-7-7-4.5L21 3Z" />
        </Svg>
      );
    case 'advisory':
      return (
        <Svg>
          <path d="M6 3.5h8.5L18 7v13.5H6ZM9 11h6M9 14.5h6M9 18h3.5" />
        </Svg>
      );
    case 'nachweis':
      return (
        <Svg>
          <path d="m20 11.5-8.2 8.2a5 5 0 0 1-7-7L13 4.5a3.3 3.3 0 0 1 4.7 4.7L9.5 17.4a1.7 1.7 0 0 1-2.4-2.4l7.6-7.6" />
        </Svg>
      );
  }
}

function SystemIcon(): ReactNode {
  return (
    <Svg size={12}>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

function ComponentIcon(): ReactNode {
  return (
    <Svg size={12}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 2.5V6M15 2.5V6M9 18v3.5M15 18v3.5M2.5 9H6M2.5 15H6M18 9h3.5M18 15h3.5" />
    </Svg>
  );
}

export default AuditLogPage;
